'use client'

// imports
;!function(){try{var e="undefined"!=typeof window?window:"undefined"!=typeof global?global:"undefined"!=typeof globalThis?globalThis:"undefined"!=typeof self?self:{};e._sentryModuleMetadata=e._sentryModuleMetadata||{},e._sentryModuleMetadata[(new e.Error).stack]=Object.assign({},e._sentryModuleMetadata[(new e.Error).stack],{"_sentryBundlerPluginAppKey:dotorg-next":true});}catch(e){}}();import { usePathname } from 'next/navigation';
import React, { ReactElement, useState, useRef, useEffect, KeyboardEvent } from 'react';

// lib files
import { continueWatching } from '@/lib/resources/continue-watching';
import { ShowDetails } from '@/lib/types/api/show-data';
import useHashTab from '@/lib/hooks/useHashTab';
import { usePersonalizedResource } from '@/lib/hooks/usePersonalizedResource';

// components
import Tab from '@/components/Tab/Tab';

// styles
import styles from './TabContainer.module.scss';

export interface TabData {
  title: string;
  // used as the URL hash for the tab, e.g. #episodes
  slug?: string;
  content: ReactElement;
}
export interface TabContainerProps {
  tabs: TabData[];
  defaultActiveTab?: number;
  defaultTabSlug?: string;
  show?: ShowDetails;
}

const TabContainerProps: React.FC<TabContainerProps> = ({tabs, defaultActiveTab = 1, defaultTabSlug, show}) => {
  const watching = usePersonalizedResource(continueWatching);
  const slugs = tabs.flatMap((tab) => tab.slug ? [tab.slug] : []);
  const isUsingHashTabs = slugs.length > 0;
  const hashSlug = useHashTab(slugs);
  const [shouldDefaultToSpecialsTab, setShouldDefaultToSpecialsTab] = useState(false);
  // defaults selected tab to be the first in the list
  const [selectedTab, setSelectedTab] = useState(defaultActiveTab);
  const [onDefaultTab, setOnDefaultTab] = useState(true);
  const pathname = usePathname();
  const tabsRef = useRef<HTMLDivElement>(null);

  const fallbackSlug = shouldDefaultToSpecialsTab && slugs.includes('specials') ? 'specials' : defaultTabSlug;
  const activeSlug = hashSlug ?? fallbackSlug;
  const activeSlugIndex = tabs.findIndex((tab) => tab.slug === activeSlug) + 1;
  // Determine active tab: URL hash, then continue watching specials, then the server default
  const activeTab = isUsingHashTabs ? (activeSlugIndex || defaultActiveTab) : selectedTab;
  const activeTabRef = useRef<number>(activeTab);

  const selectTab = (index: number) => {
    setOnDefaultTab(false);
    const slug = tabs[index - 1]?.slug;
    if (isUsingHashTabs && slug) {
      // In order to play well with Next.js and the browser history, 
      // we need to use `history.pushState` to change the URL hash without reloading the page.
      // Also, since pushState does not fire the `hashchange` event, we need to do it manually.
      // See last line of this section: https://developer.mozilla.org/en-US/docs/Web/API/History/pushState#description
      window.history.pushState(null, '', `#${slug}`);
      window.dispatchEvent(new HashChangeEvent('hashchange'));
    } else {
      setSelectedTab(index);
    }
  }

  const handleTabClick = (event: React.MouseEvent<HTMLAnchorElement>, index: number) => {
    // prevents the page from jumping to the corresponding section on tab clicks
    event.preventDefault();
    // update current tab to be the active tab
    selectTab(index);
  }

  const handleKeyPress = (event: KeyboardEvent) => {
    // left arrow moves one tab to the left
    if (event.key === 'ArrowLeft' && activeTab > 1) {
      selectTab(activeTab - 1);
    // right arrow moves one tab to the right
    } else if (event.key === 'ArrowRight' && activeTab < tabs.length) {
      selectTab(activeTab + 1);
    // shift + tab moves focus to active tab
    } else if (event.key === 'ArrowDown') {
      // Find the active tab's panel
      const activePanelId = `section${activeTab}`;
      const activePanel = document.getElementById(activePanelId);
      if (activePanel) {
        activePanel.focus();
      };
    };
  };

  useEffect(() => {
    // Defaults (on first render only) to the specials tab if all the following conditions are met:
    // 1. The user is logged in
    // 2. The current page is a show page
    // 3. The show is in the continue watching list
    // 4. The continue watching video is a special
    // find() answers for a signed-in user only, so there is no sign-in check to
    // get wrong here. The one this replaces was `profile !== undefined`, which
    // was always true.
    const continueWatchingVideo = watching.find((video) => video.show.slug === show?.slug);
    const isShowPage = pathname?.startsWith("/show/") ?? false;
    const isShowInContinueWatchingData = continueWatchingVideo !== undefined;
    setShouldDefaultToSpecialsTab(
      isShowInContinueWatchingData &&
      continueWatchingVideo.parent.resource_type === 'special' &&
      isShowPage
    );
    // deliberately only running this after the first render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Update focus to currently selected tab by finding the
    // element with aria-selected="true" and focusing on it
    const selectedTabElement = tabsRef.current?.querySelector('[aria-selected="true"]') as HTMLElement;
    if (selectedTabElement) {
      selectedTabElement.focus();
    }
    activeTabRef.current = activeTab;
  }, [activeTab]);

  return (
    <div className={styles.tab_container} ref={tabsRef} data-sentry-component="TabContainerProps" data-sentry-source-file="TabContainer.tsx">
        <ul className={styles.tabs} role={'tablist'}>
          {tabs.map((tab, index)=>{
            // Using one-based indexing here since it translates to better
            // readbility for the user when assigning ARIA labels
            index++
            if (tab.title && tab.content) {
              return (
                <Tab
                  key={`tab-${index}`}
                  title={tab.title}
                  slug={tab.slug}
                  handleClick={(e)=>{handleTabClick(e, index)}}
                  handleKeyPress={(e) => handleKeyPress(e)}
                  onDefaultTab={onDefaultTab}
                  isActive={activeTab === index}
                  index={index}
                />
              )
            }
          })}
        </ul>
        <div className={styles.tab_sections}>
          {tabs.map((tab, index)=>{
            index++
            const isActive = index === activeTab
            if (tab.title && tab.content) {
              return (
                <section
                  key={`$tab-panel-${index}`}
                  aria-labelledby={`tab${index}`}
                  hidden={!isActive ? true : undefined}
                  className={styles.tab_panel}
                  id={`section${index}`}
                  role="tabpanel"
                  tabIndex={-1} >
                {tab.content}
                </section>
              )
            }
          })}
        </div>
      </div>
    );
}

export default TabContainerProps;
